import React from "react";
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Users, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { Script } from "./ScriptCard";

interface ScriptToGameAdapterProps {
  script: Script;
  index: number;
}

const ScriptToGameAdapter: React.FC<ScriptToGameAdapterProps> = ({ script, index }) => {
  // 提取游戏时长中的小时数
  const getHoursText = (playTimeStr: string | undefined) => {
    if (!playTimeStr) return null;
    
    // 尝试从字符串中提取小时数
    const hourMatch = playTimeStr.match(/(\d+(?:\.\d+)?)\s*(?:Hours?|小时)/i);
    if (!hourMatch) return playTimeStr; // 如果无法解析，返回原始字符串
    
    const hours = parseFloat(hourMatch[1]);
    // 如果小时数为0，返回null以不显示
    if (hours === 0) return null;
    
    return hours <= 1 ? `${hours} hour` : `${hours} hours`;
  };
  
  // 检查是否显示玩家数量
  const shouldShowPlayers = () => {
    if (!script.players) return false;
    
    // 如果players字段包含"0 players"或者以"0"开头，则不显示
    return !(script.players.includes("0 players") || /^0\s/.test(script.players));
  };
  
  // 获取时长文本，可能为null
  const hoursText = getHoursText(script.playTime);

  // 获取标签颜色
  const getTagColor = (type: string) => {
    const colors: Record<string, string> = {
      "Horror": "bg-red-500/10 text-red-400 border-red-500/20",
      "Deduction": "bg-blue-500/10 text-blue-400 border-blue-500/20",
      "Fun": "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      "Rule-based": "bg-purple-500/10 text-purple-400 border-purple-500/20",
      "Emotional": "bg-green-500/10 text-green-400 border-green-500/20",
      "General": "bg-gray-500/10 text-gray-400 border-gray-500/20"
    };

    return colors[type] || colors["General"];
  };
  
  return (
    <motion.div
      className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 bg-card border border-card-border hover:-translate-y-2 w-[240px] md:w-[280px] lg:w-[300px] flex-shrink-0 snap-start flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link 
        to={script.link || `/script/${script.stableId || ''}/${script.id}`}
        className="block h-full group flex flex-col"
      >
        {/* 图片部分 */}
        <div className="aspect-[3/4] overflow-hidden">
          <img 
            src={script.image} 
            alt={script.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
          />
        </div>
        
        {/* 内容部分 */}
        <div className="p-4 flex-grow flex flex-col">
          <h3 className="text-xl font-bold mb-1 line-clamp-1">{script.title}</h3>
          
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="outline" className={`${getTagColor(script.type)} transition-all duration-300 group-hover:border-opacity-100`}>
              {script.type}
            </Badge>
            
            {script.secondaryType && (
              <Badge variant="outline" className={`${getTagColor(script.secondaryType)} transition-all duration-300 group-hover:border-opacity-100`}>
                {script.secondaryType}
              </Badge>
            )}
            
            {script.tertiaryType && (
              <Badge variant="outline" className={`${getTagColor(script.tertiaryType)} transition-all duration-300 group-hover:border-opacity-100`}>
                {script.tertiaryType}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center text-sm gap-x-3 mt-auto">
            {shouldShowPlayers() && (
              <div className="flex items-center">
                <Users className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                <span className="text-muted-foreground">{script.players}</span>
              </div>
            )}
            
            {hoursText && (
              <div className="flex items-center">
                <Clock className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                <span className="text-muted-foreground">{hoursText}</span>
              </div>
            )}
            
            <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 transition-all duration-300 group-hover:bg-purple-500/20 ml-auto">
              Offline
            </Badge>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ScriptToGameAdapter; 